import { TrackOrderApiResult } from "./tracker-service.interface";
import { HttpService } from "./http-service";
import { IService } from "./service.interface";
import { Data } from "../models/data";
import { Util } from "../utils/util";
import { LatLng } from "./maps/map-provider.interface";

export class TrackerService implements IService {
    private readonly _http: HttpService;
    private readonly _data: Data;
    private readonly _apiTracker: string;

    constructor(http: HttpService, data: Data, apiTracker: string) {
        this._http = http;
        this._data = data;
        this._apiTracker = apiTracker;
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: { showProgress?: boolean, noCache?: boolean }): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, { showProgress: options?.showProgress, noCache: options?.noCache });
    }

    /**
     * Fetches tracker data for the specified order ID.
     * @async
     * @param {number} orderId - The ID of the order for which to fetch tracker data.
     * @returns {Promise<IFetchTrackerDataResponse|null>} - A Promise that resolves with the tracker data, or null if the request fails.
     */
    public fetchTrackerData = async (storeKey: string, orderId: number): Promise<TrackOrderApiResult | null> => {
        const response = await this.httpRequestAsync("POST", this._apiTracker, { storeKey: storeKey, orderId: orderId });
        return (response.status == 200) ? await this._http.getJson(response, {parseDates: true}) : null;
    }

    /**
     * Fetches tracker user data for the specified order ID.
     * @async
     * @param {number} orderId - The ID of the order for which to fetch tracker user data.
     * @returns {Promise<IFetchTrackerDataResponse|null>} - A Promise that resolves with the tracker user data, or null if the request fails.
     */
    public fetchTrackerUserData = async (storeKey: string, orderId: number): Promise<TrackOrderApiResult | null> => {
        const response = await this.httpRequestAsync("POST", `${this._apiTracker}user`, { storeKey: storeKey, orderId: orderId });
        return (response.status == 200) ? await this._http.getJson(response, { parseDates: true}) : null;
    }

    public fetchTrackerDriverData = async (storeKey: string, orderId: number): Promise<LatLng | null> => {
        const response = await this.httpRequestAsync("POST", `${this._apiTracker}driver`, { storeKey: storeKey, orderId: orderId });
        const data = (response.status == 200) ? await this._http.getJson(response) : null;
        if (!data)
            return null;

        return {
            lat: data.latitude,
            lng: data.longitude
        };
    }
}